/**
 * ReversePaymentModal — Admin action to reverse (book-reverse) a RECORDED payment.
 *
 * @see AC-A4 in docs/specifications/2026-05-20-unified-payment-ledger/spec.md
 *
 * UX spec:
 *  - Outline-red trigger button in OrderDetailModal (rendered outside this component).
 *  - Solid-red confirm button in modal (M-UX2).
 *  - Textarea with min 20 chars + live counter X/20 colored red when < 20 (H-19).
 *  - Submit disabled until reason.length >= 20.
 *  - Focus trap + aria-labelledby (visible h3) + aria-describedby (M-UX6).
 *  - UI WARNING if > 90 days since receivedAt (L-12).
 *  - On success: toast "Płatność cofnięta", onClose(), invalidate order cache (handled by hook).
 *  - On 409 REVERSAL_TOO_LATE: offer force=true retry (owner-only — role 'admin').
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import type { BillingPayment } from "../../../../types/adminBilling";
import { useReversePaymentMutation } from "../../../../hooks/useAdminProductCatalog";
import { useToast } from "../../../../contexts/ToastContext";
import { formatDate } from "../utils/orderFormatters";

// =============================================================================
// CONSTANTS
// =============================================================================

const REASON_MIN_LENGTH = 20;
/** Days threshold after which we show a UI warning (L-12). */
const LATE_WARNING_DAYS = 90;
/** Error code returned by backend for payments older than 180 days (M-S4). */
const REVERSAL_TOO_LATE_CODE = "REVERSAL_TOO_LATE";

// =============================================================================
// TYPES
// =============================================================================

export interface ReversePaymentModalProps {
  open: boolean;
  onClose: () => void;
  payment: BillingPayment;
  orderId: string;
  brandId: string;
  /** Whether the current user has the global admin role (for force=true override). */
  isAdmin: boolean;
  /** Ref to the trigger button — focus is returned here on close (WCAG 2.4.3). */
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /**
   * Optional: order status. Used to show an AC-A7 warning when reversing a
   * payment for a FULFILLED order (subscription is active → tenant will be
   * emailed asking for a re-payment).
   */
  orderStatus?: string;
}

// =============================================================================
// HELPERS
// =============================================================================

function daysSince(isoDate: string): number {
  const then = new Date(isoDate).getTime();
  const now = Date.now();
  return Math.floor((now - then) / (1000 * 60 * 60 * 24));
}

// =============================================================================
// COMPONENT
// =============================================================================

export const ReversePaymentModal: React.FC<ReversePaymentModalProps> = ({
  open,
  onClose,
  payment,
  orderId,
  brandId,
  isAdmin,
  triggerRef,
  orderStatus,
}) => {
  const [reason, setReason] = useState("");
  const [showForceConfirm, setShowForceConfirm] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { addToast } = useToast();

  // Stable refs so handlers don't capture stale values (parents don't always memoize onClose).
  const onCloseRef = useRef(onClose);
  const triggerRefRef = useRef(triggerRef);
  useEffect(() => {
    onCloseRef.current = onClose;
    triggerRefRef.current = triggerRef;
  }, [onClose, triggerRef]);

  // Wrapped close that returns focus to the trigger button (WCAG 2.4.3).
  // We use requestAnimationFrame instead of setTimeout(..., 0) so the focus
  // call lands AFTER the browser has applied the unmount/paint that follows
  // onClose() — avoids a layout/focus race in some browsers (LOW code-quality).
  const handleClose = useCallback(() => {
    onCloseRef.current();
    requestAnimationFrame(() => {
      triggerRefRef.current?.current?.focus();
    });
  }, []);

  const reverseMutation = useReversePaymentMutation(payment.id, orderId, brandId);

  const daysDiff = daysSince(payment.receivedAt);
  const showLateWarning = daysDiff > LATE_WARNING_DAYS;

  const canSubmit = reason.length >= REASON_MIN_LENGTH && !reverseMutation.isPending;
  const reasonCountColor = reason.length < REASON_MIN_LENGTH ? "text-red-500" : "text-neutral-400";

  // Auto-focus textarea on open
  useEffect(() => {
    if (open) {
      setReason("");
      setShowForceConfirm(false);
      // Focus textarea on next tick after render
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [open]);

  // Escape-to-close (returns focus to trigger via handleClose).
  // stopPropagation() prevents the ESC from bubbling to the parent OrderDetailModal
  // (which also listens for ESC and would otherwise close itself simultaneously).
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscape, true);
    return () => document.removeEventListener("keydown", handleEscape, true);
  }, [open, handleClose]);

  // Focus trap
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    const modal = e.currentTarget;
    const focusable = modal.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleSubmit = async (force = false) => {
    try {
      await reverseMutation.mutateAsync({ reason, force });
      // Success toast is fired by useReversePaymentMutation onSuccess
      handleClose();
    } catch (err: unknown) {
      // Check for REVERSAL_TOO_LATE (409)
      const axiosErr = err as { response?: { data?: { error?: string } }; message?: string };
      const errorCode = axiosErr?.response?.data?.error;
      if (errorCode === REVERSAL_TOO_LATE_CODE && isAdmin) {
        setShowForceConfirm(true);
      } else {
        const message =
          axiosErr?.response?.data?.error === REVERSAL_TOO_LATE_CODE
            ? "Płatność jest zbyt stara — cofnięcie wymaga uprawnień administratora"
            : (axiosErr?.response?.data as { message?: string })?.message ??
              "Nie udało się cofnąć płatności";
        addToast({ message, type: "error" });
      }
    }
  };

  if (!open) return null;

  const modalDescId = `reverse-payment-desc-${payment.id}`;
  const modalTitleId = `reverse-payment-title-${payment.id}`;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        aria-describedby={modalDescId}
        onKeyDown={handleKeyDown}
        className="bg-white rounded-lg max-w-md w-full mx-4 shadow-xl"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <h3 id={modalTitleId} className="text-lg font-semibold text-neutral-900">Cofnij płatność</h3>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Payment info */}
          <p id={modalDescId} className="text-sm text-neutral-600">
            Operacja jest wyłącznie księgowa — nie powoduje zwrotu środków do klienta (Faza C).
            Data płatności: <span className="font-medium">{formatDate(payment.receivedAt)}</span>.
          </p>

          {/* AC-A7: Warning when reversing payment for a FULFILLED order
              — subscription is active, tenant will receive an email asking
              for re-payment. */}
          {orderStatus === "FULFILLED" && (
            <div
              role="alert"
              className="flex gap-2 bg-amber-50 border border-amber-200 rounded-md p-3"
            >
              <svg
                className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              <p className="text-sm text-amber-800">
                Cofnięcie spowoduje wysłanie emaila do tenanta z prośbą o
                ponowną płatność (AC-A7).
              </p>
            </div>
          )}

          {/* L-12: Late warning (> 90 days) */}
          {showLateWarning && (
            <div
              role="alert"
              className="flex gap-2 bg-amber-50 border border-amber-200 rounded-md p-3"
            >
              <svg
                className="w-5 h-5 text-amber-500 shrink-0 mt-0.5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
              <p className="text-sm text-amber-800">
                Uwaga: płatność pochodzi sprzed <strong>{daysDiff} dni</strong>.
                Cofanie starych płatności może wymagać korekty dokumentów księgowych.
              </p>
            </div>
          )}

          {/* Force confirm section (shown after REVERSAL_TOO_LATE) */}
          {showForceConfirm && (
            <div
              role="alert"
              className="bg-red-50 border border-red-200 rounded-md p-3 space-y-2"
            >
              <p className="text-sm font-medium text-red-800">
                Płatność jest starsza niż 180 dni. Czy wymusić cofnięcie?
              </p>
              <p className="text-xs text-red-700">
                To działanie jest zarezerwowane dla administratorów platformy.
              </p>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => setShowForceConfirm(false)}
                  className="px-3 py-1.5 text-xs border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  onClick={() => handleSubmit(true)}
                  disabled={reverseMutation.isPending}
                  className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-1.5"
                >
                  {reverseMutation.isPending && (
                    <div
                      className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"
                      aria-hidden="true"
                    />
                  )}
                  {reverseMutation.isPending ? "Trwa cofanie..." : "Wymuś cofnięcie"}
                </button>
              </div>
            </div>
          )}

          {/* Reason field (H-19: min 20 chars with colored counter).
              In the force-confirm phase the textarea is shown as DISABLED
              so the admin still sees the reason they typed (context retention). */}
          <div>
            <label
              htmlFor={`reverse-reason-${payment.id}`}
              className="block text-sm font-medium text-neutral-700 mb-1"
            >
              Powód cofnięcia
            </label>
            <textarea
              ref={textareaRef}
              id={`reverse-reason-${payment.id}`}
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={showForceConfirm}
              placeholder="Opisz powód cofnięcia płatności (min. 20 znaków)"
              aria-describedby={`reverse-reason-counter-${payment.id}`}
              className="w-full border border-neutral-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:border-red-400 resize-none disabled:bg-neutral-50 disabled:text-neutral-600 disabled:cursor-not-allowed"
            />
            {/* H-19: character counter colored red when < 20; aria-live so screen readers announce updates */}
            {!showForceConfirm && (
              <p
                id={`reverse-reason-counter-${payment.id}`}
                aria-live="polite"
                className={`text-xs mt-0.5 text-right ${reasonCountColor}`}
              >
                {reason.length < REASON_MIN_LENGTH
                  ? `${reason.length}/${REASON_MIN_LENGTH} (wymagane minimum)`
                  : `${reason.length} znaków`}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        {!showForceConfirm && (
          <div className="px-6 py-4 border-t border-neutral-200 flex gap-2 justify-end">
            <button
              type="button"
              onClick={handleClose}
              disabled={reverseMutation.isPending}
              className="px-4 py-2 text-sm border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50 disabled:opacity-50"
            >
              Anuluj
            </button>
            {/* M-UX2: solid-red confirm */}
            <button
              type="button"
              onClick={() => handleSubmit(false)}
              disabled={!canSubmit}
              className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center gap-2"
            >
              {reverseMutation.isPending && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" aria-hidden="true" />
              )}
              Potwierdź cofnięcie
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
